import { t } from 'i18next';

import type { ApiErrorResponse } from '~/api/Requests';
import { notificationService } from '~/services/NotificationService';
import { PICKUP_POINT_ERROR_CODES } from '~/utils/pickupPointErrors';

export class OrderErrorHandled extends Error {
  constructor() {
    super('Order error handled');
    this.name = 'OrderErrorHandled';
  }
}

const ORDER_ERROR_CODES = [
  'LESS_MIN_AMOUNT',
  'GREATER_MAX_AMOUNT',
  'CUSTOMER_PHONE_REQUIRED',
  'INVALID_PHONE_FORMAT',
  'CONSTRAINT_VIOLATION',
  'OFFER_MAX_QUANTITY_PER_ORDER_EXCEEDED',
  ...PICKUP_POINT_ERROR_CODES,
] as const;

type OrderErrorCode = (typeof ORDER_ERROR_CODES)[number];

function isKnownErrorCode(code: string): code is OrderErrorCode {
  return ORDER_ERROR_CODES.includes(code as OrderErrorCode);
}

/**
 * Attempts to handle an order error by errorCode.
 * Returns true if the error was handled, false otherwise (fallback to old logic).
 */
export function handleOrderError(responseData: ApiErrorResponse): boolean {
  // Pickup-point rejections arrive as `payload.error_code`, cart validation
  // codes as `error_code` (the field calculate uses), the rest of the order
  // errors as `errorCode`.
  const errorCode =
    responseData.data?.errorCode ??
    responseData.data?.error_code ??
    responseData.data?.payload?.error_code;
  if (!errorCode || !isKnownErrorCode(errorCode)) {
    return false;
  }

  const title = t(`orderErrors:${errorCode}`);
  const descriptionKey = `orderErrors:${errorCode}_description`;
  const descriptionParams: Record<string, unknown> = {};

  if (errorCode === 'LESS_MIN_AMOUNT') {
    descriptionParams.minAmount = responseData.data?.minAmount;
  } else if (errorCode === 'GREATER_MAX_AMOUNT') {
    descriptionParams.maxAmount = responseData.data?.maxAmount;
  }

  const description = t(descriptionKey, descriptionParams);

  notificationService.error(title, description);
  return true;
}
